import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSupabaseSEO } from '../../hooks/useSupabaseSEO';
import useSiteSettings from '../../hooks/useSiteSettings';


const injectGTM = (gtmId: string) => {
  if (!gtmId) return;
  const key = 'gtm-script';
  if (document.getElementById(key)) return;

  const script = document.createElement('script');
  script.id = key;
  script.innerHTML = `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${gtmId}');
  `;
  document.head.appendChild(script);

  const noscriptKey = 'gtm-noscript';
  if (!document.getElementById(noscriptKey)) {
    const noscript = document.createElement('noscript');
    noscript.id = noscriptKey;
    noscript.innerHTML = `
      <iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}"
      height="0" width="0" style="display:none;visibility:hidden"></iframe>
    `;
    document.body.insertBefore(noscript, document.body.firstChild);
  }
};

const injectGA = (gaId: string) => {
  if (!gaId) return;
  const key = 'ga-script-tag';
  if (document.getElementById(key)) return;

  const script = document.createElement('script');
  script.id = key;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
  document.head.appendChild(script);

  const initScript = document.createElement('script');
  initScript.id = 'ga-init-script';
  initScript.innerHTML = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${gaId}');
  `;
  document.head.appendChild(initScript);
};

const injectPixel = (pixelId: string) => {
  if (!pixelId) return;
  const key = 'fb-pixel-script';
  if (document.getElementById(key)) return;

  const script = document.createElement('script');
  script.id = key;
  script.innerHTML = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${pixelId}');
    fbq('track', 'PageView');
  `;
  document.head.appendChild(script);

  const noscriptKey = 'fb-pixel-noscript';
  if (!document.getElementById(noscriptKey)) {
    const noscript = document.createElement('noscript');
    noscript.id = noscriptKey;
    noscript.innerHTML = `
      <img height="1" width="1" style="display:none"
      src="https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1" />
    `;
    document.body.appendChild(noscript);
  }
};

const injectSearchConsole = (verificationId: string) => {
  if (!verificationId) return;
  const key = 'google-site-verification';
  let code = verificationId;
  if (verificationId.includes('content=')) {
    const match = verificationId.match(/content="([^"]+)"/) || verificationId.match(/content='([^']+)'/);
    if (match && match[1]) {
      code = match[1];
    }
  }
  let meta = document.querySelector(`meta[name="${key}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', key);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', code);
};

const injectHTML = (htmlString: string, target: HTMLElement, attributeName: string) => {
  const prevElements = target.querySelectorAll(`[data-injected="${attributeName}"]`);
  prevElements.forEach(el => el.remove());

  if (!htmlString) return;

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${htmlString}</div>`, 'text/html');
  const container = doc.body.firstChild as HTMLElement;
  if (!container) return;

  Array.from(container.childNodes).forEach(node => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (el.tagName.toLowerCase() === 'script') {
        const script = document.createElement('script');
        script.setAttribute('data-injected', attributeName);
        Array.from(el.attributes).forEach(attr => script.setAttribute(attr.name, attr.value));
        script.innerHTML = el.innerHTML;
        target.appendChild(script);
      } else {
        const clone = el.cloneNode(true) as HTMLElement;
        clone.setAttribute('data-injected', attributeName);
        target.appendChild(clone);
      }
    } else if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
      const span = document.createElement('span');
      span.setAttribute('data-injected', attributeName);
      span.style.display = 'none';
      span.appendChild(document.createTextNode(node.textContent));
      target.appendChild(span);
    }
  });
};

function mapPathToPageKey(pathname: string): string {
  if (pathname === '/' || pathname === '') return 'home';
  if (pathname.startsWith('/golden-tower')) return 'brand';
  if (pathname.startsWith('/residences') || pathname.startsWith('/new-releases')) return 'new-releases';
  if (pathname.startsWith('/amenities')) return 'amenities';
  if (pathname.startsWith('/blog')) return 'blog';
  if (pathname.startsWith('/contact')) return 'contact';
  return 'home';
}

const SEOHead: React.FC = () => {
  const location = useLocation();
  const pageKey = mapPathToPageKey(location.pathname || '/');
  const { seoData } = useSupabaseSEO(pageKey);
  const siteSettings = useSiteSettings();

  useEffect(() => {
    document.documentElement.lang = 'en';
    if (siteSettings) {
      // Handle favicon from site settings
      if (siteSettings.favicon_url) {
        const existingFavicons = document.querySelectorAll("link[rel*='icon']");
        existingFavicons.forEach(favicon => favicon.remove());

        const favicon = document.createElement('link');
        favicon.rel = 'icon';
        favicon.type = 'image/x-icon';
        favicon.href = siteSettings.favicon_url;
        document.head.appendChild(favicon);

        const appleTouchIcon = document.createElement('link');
        appleTouchIcon.rel = 'apple-touch-icon';
        appleTouchIcon.href = siteSettings.favicon_url;
        document.head.appendChild(appleTouchIcon);
      }

      // Inject integrations and custom scripts
      if (siteSettings.google_tag_manager_id) {
        injectGTM(siteSettings.google_tag_manager_id);
      }
      if (siteSettings.google_analytics_id) {
        injectGA(siteSettings.google_analytics_id);
      }
      if (siteSettings.facebook_pixel_id) {
        injectPixel(siteSettings.facebook_pixel_id);
      }
      if (siteSettings.search_console_id) {
        injectSearchConsole(siteSettings.search_console_id);
      }
      if (siteSettings.custom_head_code) {
        injectHTML(siteSettings.custom_head_code, document.head, 'custom-head');
      }
      if (siteSettings.custom_body_code) {
        injectHTML(siteSettings.custom_body_code, document.body, 'custom-body');
      }
    }

    if (!seoData) return;

    try {
      if (seoData.title) document.title = seoData.title;

      const ensureMeta = (attr: 'name' | 'property', key: string, content: string) => {
        if (!content) return;
        let el = document.querySelector(`meta[${attr}='${key}']`) as HTMLMetaElement | null;
        if (!el) {
          el = document.createElement('meta');
          el.setAttribute(attr, key);
          document.head.appendChild(el);
        }
        el.setAttribute('content', content);
      };

      ensureMeta('name', 'description', seoData.description || '');
      ensureMeta('name', 'keywords', seoData.keywords || '');
      ensureMeta('property', 'og:title', seoData.title || '');
      ensureMeta('property', 'og:description', seoData.description || '');
      if (seoData.og_image) ensureMeta('property', 'og:image', seoData.og_image);

      if (seoData.canonical_url) {
        let link = document.querySelector("link[rel='canonical']") as HTMLLinkElement | null;
        if (!link) {
          link = document.createElement('link');
          link.rel = 'canonical';
          document.head.appendChild(link);
        }
        link.href = seoData.canonical_url;
      }
    } catch (e) {
      // fail silently
    }
  }, [seoData, siteSettings]);

  return null;
};

export default SEOHead;

